import { useState } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { formatCurrency, formatNumber } from './format';
import { pointsToDollarsAt, type PricingRecalcOutput } from './hooks/usePricingRecalc';

interface PriceFlowDiagramProps {
  recalc: PricingRecalcOutput;
}

type Unit = 'points' | 'bps' | 'dollars';

/**
 * Node-value rendering matches the handoff's per-unit contract (see the
 * `data-points/data-bps/data-dollars` attributes on each `.flow-node` `<text>`
 * at `Product Pricing.html:3618-3643`):
 *
 *   - Points mode, `pointsMode='absolute'`: display `100 + deltaPts` as the
 *     absolute price (Base / Locked / Borrower path / Investor path — where
 *     the user expects to read "98.567" for locked, not "−1.433").
 *   - Points mode, `pointsMode='delta'`: signed par-delta (Net to Lendwise
 *     node only — the design shows `+1.183`).
 *   - BPS mode (all nodes): signed par-delta × 100.
 *   - Dollars mode (all nodes): signed par-delta × loanAmount × 0.01.
 *
 * All input is a signed par-delta in pts (e.g. `-1.433` for the Locked node
 * when the loan's basePriceLocked is 98.567).
 */
function renderUnit(unit: Unit, deltaPts: number, loanAmount: number, pointsMode: 'absolute' | 'delta'): string {
  if (unit === 'points') {
    if (pointsMode === 'absolute') {
      return formatNumber(100 + deltaPts, 3);
    }
    return `${deltaPts >= 0 ? '+' : ''}${formatNumber(deltaPts, 3)}`;
  }
  if (unit === 'bps') {
    const bps = deltaPts * 100;
    if (bps === 0) {
      return formatNumber(0, 1);
    }
    return `${bps > 0 ? '+' : ''}${formatNumber(bps, 1)}`;
  }
  const dollars = pointsToDollarsAt(loanAmount, deltaPts);
  if (dollars === 0) {
    return '$0';
  }
  const abs = formatCurrency(Math.abs(dollars));
  return (dollars > 0 ? '+' : '−') + abs;
}

/**
 * Sankey-style price flow: Base → Locked → Borrower / Investor paths → Net to
 * Lendwise. Unit toggle swaps the displayed value on every node; Gate 1
 * persistence is local state only.
 */
export function PriceFlowDiagram({ recalc }: PriceFlowDiagramProps) {
  const [unit, setUnit] = useState<Unit>('points');
  const {
    loanAmount,
    basePriceLocked,
    basePriceBeforeLlpa,
    discountPoints,
    backEndMargin,
    investorLockPrice,
    netLoLocked,
    netToLendwiseDollars,
    locrPoints,
    concessionPoints,
    lenderCreditPoints,
    lenderCreditDollars,
    curePoints,
    bemOriginalTotal,
    bemCurrentTotal,
    bemHasUpdates
  } = recalc;

  // Phase 2 QA Fix 3 — explicit point-space math for the NET ADJUSTMENTS breakdown.
  // Sign convention matches the waterfall: LOCR + concessions ADD to the borrower
  // path; lender credit + cure SUBTRACT from it. Derived here so the popover
  // shows the same point totals recalc computes internally, not a back-derivation
  // from display strings.
  const netAdjustmentsPoints =
    locrPoints + concessionPoints - lenderCreditPoints - curePoints;
  const netAdjustmentsDollars = pointsToDollarsAt(loanAmount, netAdjustmentsPoints);
  // Local helper so we don't leak a points formatter outside pricing-v2.
  const formatPoints = (n: number) => formatNumber(n, 3);

  // Every node value is a signed par-delta in pts. Absolute-points rendering
  // (the four main nodes) resolves to `100 + delta`; delta-points rendering
  // (Net to Lendwise) keeps the sign on the delta directly.
  //
  // Node ↔ equation pairing:
  //   Borrower node = netLoLocked (live waterfall endpoint — matches the
  //     ledger's "Net LO Locked price" row). Equation row:
  //       simple case (no post-lock activity beyond discount):
  //         basePriceLocked + discountPoints = netLoLocked
  //       adjusted case (concessions / LOCR / lender credit / cure in play):
  //         basePriceLocked + discountPoints ± netAdjustments = netLoLocked
  //       where netAdjustments = netLoLocked − basePriceLocked − discountPoints
  //       (signed; positive = net release into borrower path, negative = net
  //       take from borrower path via LC / cure).
  //   Investor node = fixed Encompass investor-side lock fact. When local
  //     waterfall edits reduce the current margin, render the release delta as
  //     its own term so the equation still reconciles to investorLockPrice.
  // Handoff source: Product Pricing.html:3618-3643 data-points attributes.
  const baseValue = basePriceBeforeLlpa - 100;
  const lockedValue = basePriceLocked - 100;
  const borrowerValue = netLoLocked - 100;
  // Epsilon matches `bemHasUpdates` threshold at usePricingRecalc.ts:235 —
  // canonical pricing-v2 "meaningful change" scale.
  const netAdjustments = netLoLocked - (basePriceLocked + discountPoints);
  const isSimpleBorrowerCase = Math.abs(netAdjustments) < 0.0005;
  const investorValue = investorLockPrice - 100;
  const netValue = loanAmount > 0 ? netToLendwiseDollars / (loanAmount * 0.01) : 0;
  const displayBackEndMargin = bemHasUpdates ? bemCurrentTotal : backEndMargin;
  const releasedMarginPoints = Math.max(0, bemOriginalTotal - bemCurrentTotal);

  return (
    <section className="flow-card" aria-label="Price flow">
      <div className="flow-hdr">
        <h3>Price flow · Base → Borrower / Investor paths</h3>
        <div className="flow-hdr-right">
          <span className="agg">
            Loan amount{' '}
            <b className="mono" style={{ color: 'var(--ink)' }}>
              {formatCurrency(loanAmount)}
            </b>
          </span>
          <div className="unit-toggle" role="tablist" aria-label="Display units">
            {(['points', 'bps', 'dollars'] as const).map((u) => (
              <button
                key={u}
                type="button"
                className={`ut-btn${u === unit ? ' active' : ''}`}
                role="tab"
                aria-selected={u === unit}
                onClick={() => setUnit(u)}
              >
                {u === 'points' ? 'Points' : u === 'bps' ? 'BPS' : 'Dollars'}
              </button>
            ))}
          </div>
        </div>
      </div>

      <svg className="flow-svg" viewBox="0 0 900 130" preserveAspectRatio="xMidYMid meet">
        <path className="flow-path" d="M 128 65 L 210 65" />
        <path className="flow-path borrower" d="M 322 65 C 380 65, 380 28, 440 28" />
        <path className="flow-path investor" d="M 322 65 C 380 65, 380 102, 440 102" />
        <path className="flow-path reduction" d="M 552 28 L 552 65 L 670 65" />
        <path className="flow-path reduction" d="M 552 102 L 552 65 L 670 65" />

        <g className="flow-node">
          <rect x="20" y="45" width="108" height="40" rx="5" fill="var(--surface-2)" stroke="var(--border-strong)" />
          <text className="label" x="74" y="56" textAnchor="middle">Base</text>
          <text className="value" x="74" y="76" textAnchor="middle">
            {renderUnit(unit, baseValue, loanAmount, 'absolute')}
          </text>
        </g>

        <g className="flow-node">
          <rect x="210" y="40" width="112" height="50" rx="5" fill="var(--accent-soft)" stroke="var(--accent)" />
          <text className="label" x="266" y="52" textAnchor="middle" fill="var(--accent-ink)">Locked</text>
          <text className="value" x="266" y="70" textAnchor="middle" fill="var(--accent-ink)">
            {renderUnit(unit, lockedValue, loanAmount, 'absolute')}
          </text>
          <text className="sub" x="266" y="83" textAnchor="middle" fill="var(--accent-ink)">after LLPA</text>
        </g>

        <g className="flow-node">
          <rect x="440" y="8" width="112" height="40" rx="5" fill="var(--borrower-soft)" stroke="var(--borrower)" />
          <text className="label" x="496" y="20" textAnchor="middle">Borrower path</text>
          <text className="value" x="496" y="38" textAnchor="middle">
            {renderUnit(unit, borrowerValue, loanAmount, 'absolute')}
          </text>
        </g>

        <g className="flow-node">
          <rect x="440" y="82" width="112" height="40" rx="5" fill="var(--investor-soft)" stroke="var(--investor)" />
          <text className="label" x="496" y="94" textAnchor="middle">Investor path</text>
          <text className="value" x="496" y="112" textAnchor="middle">
            {renderUnit(unit, investorValue, loanAmount, 'absolute')}
          </text>
        </g>

        <g className="flow-node">
          <rect x="670" y="45" width="170" height="40" rx="5" fill="var(--pos-soft)" stroke="var(--pos)" />
          <text className="label" x="755" y="56" textAnchor="middle" fill="var(--pos-ink)">Net to Lendwise</text>
          <text className="value" x="755" y="76" textAnchor="middle" fill="var(--pos-ink)">
            {renderUnit(unit, netValue, loanAmount, 'delta')}
          </text>
        </g>
      </svg>

      <div className="path-math">
        <div className="pm-row borrower">
          <span className="pm-tag">Borrower path</span>
          <span className="pm-eq mono tab">
            <span className="pm-base">{formatNumber(basePriceLocked, 3)}</span>
            <span className="pm-op">+</span>
            <span className="pm-delta">{formatNumber(discountPoints, 3)}</span>
            <span className="pm-delta-lbl">discount pts</span>
            {!isSimpleBorrowerCase && (
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  {/*
                    Fix 3: the whole net-adjustments segment (sign + delta + label)
                    is the hover/focus trigger. TooltipTrigger opens on hover AND
                    keyboard focus, which Popover does not (Popover needs click).
                    tabIndex makes the segment focusable for keyboard users.
                  */}
                  <TooltipTrigger asChild>
                    <span
                      role="button"
                      tabIndex={0}
                      className="pm-net-adj-trigger"
                      aria-label="Show net adjustments breakdown"
                    >
                      <span className="pm-op">{netAdjustments >= 0 ? '+' : '−'}</span>
                      <span className="pm-delta">{formatNumber(Math.abs(netAdjustments), 3)}</span>
                      <span className="pm-delta-lbl pm-delta-lbl-hoverable">net adjustments</span>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" align="start" className="pm-net-adj-popover">
                    <div className="pm-net-adj-title">Net adjustments</div>
                    <div className="pm-net-adj-rows">
                      <div className="pm-net-adj-row">
                        <span>LOCR</span>
                        <span className="pm-net-adj-val">
                          +{formatPoints(locrPoints)} pts · +{formatCurrency(pointsToDollarsAt(loanAmount, locrPoints))}
                        </span>
                      </div>
                      <div className="pm-net-adj-row">
                        <span>Concessions</span>
                        <span className="pm-net-adj-val">
                          +{formatPoints(concessionPoints)} pts · +{formatCurrency(pointsToDollarsAt(loanAmount, concessionPoints))}
                        </span>
                      </div>
                      <div className="pm-net-adj-row">
                        <span>Lender credit</span>
                        <span className="pm-net-adj-val">
                          −{formatPoints(lenderCreditPoints)} pts · −{formatCurrency(lenderCreditDollars)}
                        </span>
                      </div>
                      <div className="pm-net-adj-row">
                        <span>Cure</span>
                        <span className="pm-net-adj-val">
                          −{formatPoints(curePoints)} pts · −{formatCurrency(pointsToDollarsAt(loanAmount, curePoints))}
                        </span>
                      </div>
                      <div className="pm-net-adj-row pm-net-adj-total">
                        <span>Total</span>
                        <span className="pm-net-adj-val">
                          {netAdjustmentsPoints >= 0 ? '+' : '−'}{formatPoints(Math.abs(netAdjustmentsPoints))} pts · {netAdjustmentsDollars >= 0 ? '+' : '−'}{formatCurrency(Math.abs(netAdjustmentsDollars))}
                        </span>
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <span className="pm-op">=</span>
            <span className="pm-result">{formatNumber(netLoLocked, 3)}</span>
            <span className="pm-unit">points</span>
          </span>
        </div>
        <div className="pm-row investor">
          <span className="pm-tag">Investor path</span>
          <span className="pm-eq mono tab">
            <span className="pm-base">{formatNumber(basePriceLocked, 3)}</span>
            <span className="pm-op">+</span>
            <span className="pm-delta">{formatNumber(displayBackEndMargin, 3)}</span>
            <span className="pm-delta-lbl">back-end margin</span>
            {releasedMarginPoints > 0 && (
              <>
                <span className="pm-op">+</span>
                <span className="pm-delta">{formatNumber(releasedMarginPoints, 3)}</span>
                <span className="pm-delta-lbl">released</span>
              </>
            )}
            <span className="pm-op">=</span>
            <span className="pm-result">{formatNumber(investorLockPrice, 3)}</span>
            <span className="pm-unit">points</span>
          </span>
        </div>
      </div>
    </section>
  );
}
